import React, { useState, useEffect } from 'react';
import { SideBar } from '../../layout';
import * as mui from '@mui/material';
import SearchAppBar from '../../common/Appbar';
import Separator from '../../layout/Separator';
import ResponsiveContainer from '../../layout/Container';
import AppsIcon from '@mui/icons-material/Apps';
import HomeIcon from '@mui/icons-material/Home';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { motion } from 'framer-motion';
import { ArrowRightCircleIcon } from '@heroicons/react/20/solid';
import LogoLoader from '../../common/LogoLoader';
import PostAddIcon from '@mui/icons-material/PostAdd';
import {
    UsersIcon,
    ComputerDesktopIcon,
    BuildingOfficeIcon,
    UserGroupIcon,
    LockClosedIcon,
    Square3Stack3DIcon,
    SignalIcon,
    DocumentIcon,
} from '@heroicons/react/24/outline';

const Dashboardv2 = () => {
    const actions = [
        {
            title: 'Companies',
            href: '/Companies',
            icon: BuildingOfficeIcon,
            iconForeground: 'text-teal-700',
            iconBackground: 'bg-teal-50',
            identifier: 'COMP',
            description: 'Manage and view your company details, add new companies or edit existing ones'
        },
        // {
        //     title: 'System Roles',
        //     href: '/SystemRoles',
        //     icon: ComputerDesktopIcon,
        //     iconForeground: 'text-purple-700',
        //     iconBackground: 'bg-purple-50',
        //     identifier: 'ROLE',
        //     description: 'Configure roles for users in your system and assign them appropriate permissions.'
        // },
        {
            title: 'Users',
            href: '/ManageUsers',
            icon: UsersIcon,
            iconForeground: 'text-sky-700',
            iconBackground: 'bg-sky-50',
            identifier: 'USR',
            description: 'View, manage, and update the user accounts and their settings'
        },
        {
            title: 'Applications',
            href: '/Applications',
            icon: Square3Stack3DIcon,
            iconForeground: 'text-yellow-700',
            iconBackground: 'bg-yellow-50',
            identifier: 'APP',
            description: 'Manage applications, configure integrations, and monitor system status'
        },
        {
            title: 'HR Data Management',
            href: '/hr-data-management',
            icon: DocumentIcon,
            iconForeground: 'text-green-700',
            iconBackground: 'bg-green-50',
            identifier: 'HR',
            description: 'Manage HR data through manual uploads or automated SFTP jobs'
        },
        {
            title: 'Security',
            href: '/Security',
            icon: LockClosedIcon,
            iconForeground: 'text-rose-700',
            iconBackground: 'bg-rose-50',
            identifier: 'SEC',
            description: 'Set up and manage security protocols for your system'
        },
        {
            title: 'Employee Record',
            href: '/hrrecord',
            icon: UserGroupIcon,
            iconForeground: 'text-indigo-700',
            iconBackground: 'bg-indigo-50',
            identifier: 'EMP',
            description: 'Configure HR interfaces and API integrations for your system'
        },
        {
            title: 'Network Layer Setup',
            href: '/networklayer',
            icon: SignalIcon,
            iconForeground: 'text-orange-700',
            iconBackground: 'bg-orange-50',
            identifier: 'NET',
            description: 'Configure network interfaces and manage automated access management'
        },
        {
            title: 'Blog Manager',
            href: '/blogmanager',
            icon: PostAddIcon,
            iconForeground: 'text-purple-700',
            iconBackground: 'bg-purple-50',
            identifier: 'BLOG',
            description: 'Create and manage blog content, publish articles, and engage with your audience'
        },
    ];

    const [isLoggedIn, setIsLoggedIn] = useState(true);
    const [displayMode, setDisplayMode] = useState('grid'); // 'grid' or 'list'
    const [isLoading, setIsLoading] = useState(true);
    
    useEffect(() => {
        // Simulate loading delay
        const loadData = async () => {
            await new Promise(resolve => setTimeout(resolve, 1000));
            setIsLoading(false);
        };
        
        loadData();
    }, []);

    function classNames(...classes) {
        return classes.filter(Boolean).join(' ');
    }

    // Function to get initials from title
    function getInitials(name) {
        return name
            .split(' ')
            .map(word => word[0].toUpperCase())
            .join('');
    }

    // Function to get a fixed color based on title (for consistency)
    function getColorForTitle(title) {
        const colors = [
            'bg-pink-600',
            'bg-purple-600',
            'bg-yellow-500',
            'bg-green-500',
            'bg-blue-500',
            'bg-red-500',
            'bg-teal-500',
            'bg-indigo-600',
            'bg-orange-500',
        ];
        // Use a hash function to get a consistent color for each title
        let hash = 0;
        for (let i = 0; i < title.length; i++) {
            hash = title.charCodeAt(i) + ((hash << 5) - hash);
        }
        hash = Math.abs(hash);
        return colors[hash % colors.length];
    }

    // Animation variants for staggered entry
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1
            }
        }
    };

    const itemVariants = {
        hidden: { y: 20, opacity: 0 },
        visible: {
            y: 0,
            opacity: 1,
            transition: {
                type: "spring",
                stiffness: 100,
                damping: 12
            }
        }
    };

    const toggleDisplayMode = () => {
        setDisplayMode(displayMode === 'grid' ? 'list' : 'grid');
    };

    const customMainContent = (
        <div className="min-h-screen bg-gray-50">
            <ResponsiveContainer>
                <mui.Box sx={{ padding: '24px' }}>
                <mui.Breadcrumbs aria-label="breadcrumb">
                        <mui.Link underline="hover" color="inherit" href="/Dashboard" sx={{ display: 'flex', alignItems: 'center' }}>
                            <HomeIcon sx={{ mr: 0.5, fontSize: '1.1rem' }} />
                            Home
                    </mui.Link>
                    <mui.Link underline="hover" color="inherit" href="/Dashboard">
                        Dashboard
                    </mui.Link>
                    <mui.Typography color="text.primary">System Settings</mui.Typography>
                </mui.Breadcrumbs>

                    <mui.Box sx={{ display: 'flex', alignItems: 'center', mb: 3, mt: 2 }}>
                        <AppsIcon sx={{ fontSize: 28, color: 'primary.main', mr: 2 }} />
                        <mui.Box sx={{ flexGrow: 1 }}>
                            <mui.Typography variant="h5" component="h1" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
                                System Settings
                            </mui.Typography>
                            <mui.Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Manage users, integrations, system security, and other settings
                </mui.Typography>
                        </mui.Box>
                        <mui.ButtonGroup size="small" sx={{ ml: 2 }}>
                            <mui.Button 
                                variant={displayMode === 'grid' ? "contained" : "outlined"}
                                onClick={() => setDisplayMode('grid')}
                                startIcon={<mui.Icon>grid_view</mui.Icon>}
                                sx={{ textTransform: 'none' }}
                            >
                                Grid
                            </mui.Button>
                            <mui.Button 
                                variant={displayMode === 'list' ? "contained" : "outlined"}
                                onClick={() => setDisplayMode('list')}
                                startIcon={<mui.Icon>view_list</mui.Icon>}
                                sx={{ textTransform: 'none' }}
                            >
                                List
                            </mui.Button>
                        </mui.ButtonGroup>
                    </mui.Box>

                <Separator />

                    <mui.Box sx={{ 
                        backgroundColor: '#f8fafc', 
                        borderRadius: 2,
                        minHeight: '70vh',
                        mt: 3,
                        boxShadow: 'inset 0 1px 2px 0 rgb(0 0 0 / 0.05)'
                    }}>
                        <mui.Paper 
                            elevation={0}
                            sx={{ 
                                p: 2,
                                height: '100%',
                                backgroundColor: 'transparent'
                            }}
                        >
                            {displayMode === 'grid' ? (
                                <motion.div 
                                    className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 mt-2"
                                    variants={containerVariants}
                                    initial="hidden"
                                    animate="visible"
                                >
                                    {actions.map((action, actionIdx) => (
                                        <motion.div 
                                            key={action.title}
                                            variants={itemVariants}
                                            whileHover={{ 
                                                scale: 1.03, 
                                                boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.05)" 
                                            }}
                                            transition={{ type: "spring", stiffness: 400, damping: 17 }}
                                        >
                                            <mui.Paper
                                                component={mui.Link}
                                                href={action.href}
                                                sx={{
                                                    p: 2,
                                                    height: '100%',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    borderRadius: 1.5,
                                                    backgroundColor: '#ffffff',
                                                    textDecoration: 'none',
                                                    cursor: 'pointer',
                                                    overflow: 'hidden',
                                                    transition: 'all 0.2s ease-in-out',
                                                    boxShadow: '0 1px 2px 0 rgba(0,0,0,0.03)',
                                                    border: '1px solid',
                                                    borderColor: 'rgba(0, 0, 0, 0.04)',
                                                    '&:hover': {
                                                        backgroundColor: '#ffffff',
                                                        borderColor: 'rgba(25, 118, 210, 0.1)'
                                                    }
                                                }}
                                            >
                                                <mui.Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                                <span
                                    className={classNames(
                                        action.iconBackground,
                                        action.iconForeground,
                                                            'inline-flex rounded-md p-1.5 ring-2 ring-white'
                                    )}
                                >
                                                        <action.icon className="size-4" aria-hidden="true" />
                                </span>
                                                    <mui.Typography 
                                                        variant="subtitle1" 
                                                        component="h3" 
                                                        sx={{ 
                                                            fontWeight: 600, 
                                                            color: 'text.primary',
                                                            fontSize: '0.9rem',
                                                            ml: 1.5
                                                        }}
                                                    >
                                    {action.title}
                                                    </mui.Typography>
                                                </mui.Box>
                                                
                                                <mui.Typography 
                                                    variant="body2" 
                                                    sx={{ 
                                                        color: 'text.secondary',
                                                        fontSize: '0.75rem',
                                                        lineHeight: 1.4,
                                                        mb: 1.5,
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        display: '-webkit-box',
                                                        WebkitLineClamp: 2,
                                                        WebkitBoxOrient: 'vertical'
                                                    }}
                                                >
                                    {action.description}
                                                </mui.Typography>
                                                
                                                <mui.Box 
                                                    sx={{ 
                                                        mt: 'auto',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'flex-end',
                                                        color: 'primary.main',
                                                        fontSize: '0.7rem',
                                                        fontWeight: 600
                                                    }}
                                                >
                                                    <mui.Box 
                                                        component="span" 
                                                        sx={{ 
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            transition: 'transform 0.2s ease-in-out',
                                                            transform: 'translateX(0)',
                                                            '.MuiPaper-root:hover &': {
                                                                transform: 'translateX(2px)'
                                                            }
                                                        }}
                                                    >
                                                        <OpenInNewIcon sx={{ fontSize: 14, ml: 0.3 }} />
                                                    </mui.Box>
                                                </mui.Box>
                                            </mui.Paper>
                                        </motion.div>
                                    ))}
                                </motion.div>
                            ) : (
                                <motion.ul 
                                    className="divide-y divide-gray-100 rounded-md overflow-hidden bg-white shadow-sm mt-2"
                                    variants={containerVariants}
                                    initial="hidden"
                                    animate="visible"
                                >
                                    {actions.map((action) => {
                                        const bgColor = getColorForTitle(action.title);
                                        return (
                                            <motion.li 
                                                key={action.title} 
                                                className="hover:bg-gray-50 transition-colors"
                                                variants={itemVariants}
                                                whileHover={{ backgroundColor: '#f9fafb' }}
                                            >
                                                <a 
                                                    href={action.href} 
                                                    className="flex items-center py-2.5 px-4"
                                                    style={{ textDecoration: 'none' }}
                                                >
                                                    <div
                                                        className={classNames(
                                                            bgColor,
                                                            'flex w-8 h-8 shrink-0 items-center justify-center rounded-md text-xs font-medium text-white'
                                                        )}
                                                    >
                                                        {action.identifier}
                                                    </div>
                                                    <div className="ml-3 flex-1 min-w-0">
                                                        <div className="text-sm font-medium text-gray-900 truncate">{action.title}</div>
                                                        <div className="text-xs text-gray-500 truncate mt-0.5">{action.description}</div>
                </div>
                                                    <ArrowRightCircleIcon className="h-4 w-4 text-gray-400" aria-hidden="true" />
                                                </a>
                                            </motion.li>
                                        );
                                    })}
                                </motion.ul>
                            )}
                        </mui.Paper>
                    </mui.Box>
                </mui.Box>
            </ResponsiveContainer>
        </div>
    );

    return (
        <div>
            {isLoading ? (
                <mui.Box display="flex" justifyContent="center" alignItems="center" height="100vh">
                    <LogoLoader 
                        size={80} 
                        message="Loading Dashboard..." 
                        duration={1.5} 
                    />
                </mui.Box>
            ) : (
                <SideBar mainContent={customMainContent} />
            )}
        </div>
    );
}

export default Dashboardv2;
